import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { EnvelopeSimple, PaperPlaneRight, EnvelopeOpen, Circle, Plus, UsersThree, User, Paperclip, File as FileIcon, X, MagnifyingGlass, Smiley, Bell, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import EmojiPicker from "emoji-picker-react";
import MentionTextarea from "../components/MentionTextarea";
import EmojiButton from "../components/EmojiButton";

export default function MessagingPage() {
  const { user } = useAuth();
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sendMode, setSendMode] = useState("individual");
  const [form, setForm] = useState({ recipient_id: "", recipient_ids: [], recipient_group: "", subject: "", content: "", attachment_url: "", attachment_name: "" });
  const [replyText, setReplyText] = useState("");
  const [replyAttachment, setReplyAttachment] = useState({ url: "", name: "" });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const chatEndRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  const fileInputRef = useRef(null);
  const replyFileRef = useRef(null);

  const userId = user?.id || user?._id;

  const fetchMessages = () => {
    api.get("/messages/inbox").then(r => { setInbox(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/messages/sent").then(r => setSent(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchMessages();
    api.get("/users/directory").then(r => setDirectory(r.data)).catch(() => {});
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  // Build conversation list from inbox + sent
  const conversations = (() => {
    const map = new Map();
    [...inbox, ...sent].forEach(msg => {
      const otherId = msg.sender_id === userId ? msg.recipient_id : msg.sender_id;
      const otherName = msg.sender_id === userId ? msg.recipient_name : msg.sender_name;
      const key = otherId;
      if (!map.has(key) || new Date(msg.created_at) > new Date(map.get(key).created_at)) {
        map.set(key, { ...msg, other_id: otherId, other_name: otherName });
      }
    });
    return Array.from(map.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  })();

  const filteredConversations = conversations.filter(c =>
    c.other_name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.subject?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const unreadCount = inbox.filter(m => !m.read).length;

  const openConversation = async (conv) => {
    setSelectedMsg(conv);
    try {
      const { data } = await api.get(`/messages/${conv.id}/thread`);
      setThread(data.length > 0 ? data : [conv]);
      // Mark as read
      if (conv.recipient_id === userId && !conv.read) {
        await api.get(`/messages/${conv.id}`);
        fetchMessages();
      }
    } catch {
      setThread([conv]);
    }
  };

  const resetForm = () => {
    setForm({ recipient_id: "", recipient_ids: [], recipient_group: "", subject: "", content: "", attachment_url: "", attachment_name: "" });
    setSendMode("individual");
    setSearchQuery("");
  };

  const handleUpload = async (file, isReply = false) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (isReply) {
        setReplyAttachment({ url: res.data.url, name: file.name });
      } else {
        setForm(prev => ({ ...prev, attachment_url: res.data.url, attachment_name: file.name }));
      }
    } catch { toast.error("Failed to upload file"); }
    setUploading(false);
  };

  const sendMessage = async () => {
    if (!form.subject || !form.content) { toast.error("Subject and message are required"); return; }
    if (sendMode === "individual" && !form.recipient_id) { toast.error("Please select a recipient"); return; }
    if (sendMode === "multiple" && form.recipient_ids.length === 0) { toast.error("Select at least one recipient"); return; }
    if (sendMode === "group" && !form.recipient_group) { toast.error("Select a group"); return; }
    setSending(true);
    try {
      const payload = { subject: form.subject, content: form.content, attachment_url: form.attachment_url, attachment_name: form.attachment_name };
      if (sendMode === "individual") payload.recipient_id = form.recipient_id;
      else if (sendMode === "multiple") payload.recipient_ids = form.recipient_ids;
      else if (sendMode === "group") payload.recipient_group = form.recipient_group;
      await api.post("/messages", payload);
      toast.success("Message sent!");
      setComposeOpen(false);
      resetForm();
      fetchMessages();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    setSending(false);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedMsg) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/${selectedMsg.id}/reply`, {
        content: replyText,
        attachment_url: replyAttachment.url,
        attachment_name: replyAttachment.name,
      });
      setThread(prev => [...prev, data]);
      setReplyText("");
      setReplyAttachment({ url: "", name: "" });
      fetchMessages();
    } catch { toast.error("Failed to send reply"); }
    setSending(false);
  };

  const toggleRecipient = (id) => {
    setForm(prev => ({
      ...prev,
      recipient_ids: prev.recipient_ids.includes(id) ? prev.recipient_ids.filter(r => r !== id) : [...prev.recipient_ids, id],
    }));
  };

  const otherUsers = directory.filter(u => u.id !== userId);
  const filteredUsers = otherUsers.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const groupOptions = [
    { value: "all_students", label: "All Students" },
    { value: "all_instructors", label: "All Instructors" },
    { value: "all_admins", label: "All Admins" },
    { value: "all_users", label: "All Users" },
  ];

  return (
    <Layout>
      <div className="flex h-full overflow-hidden" data-testid="messaging-page">
        {/* Sidebar — Conversations List. Hidden on mobile when a conversation is open */}
        <div className={`w-full sm:w-80 border-r border-slate-200 bg-white flex flex-col flex-shrink-0 ${selectedMsg ? "hidden sm:flex" : "flex"}`}>
          <div className="p-3 border-b border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Messages</h2>
              <Dialog open={composeOpen} onOpenChange={(open) => { setComposeOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-800 text-white hover:bg-blue-900 rounded-none h-8 px-3 text-xs gap-1" data-testid="compose-message-btn">
                    <Plus size={14} weight="bold" /> New
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg rounded-none max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>New Message</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div className="flex gap-1 bg-slate-100 p-1">
                      {[
                        { key: "individual", label: "Individual", icon: <User size={14} /> },
                        { key: "multiple", label: "Multiple", icon: <UsersThree size={14} /> },
                        { key: "group", label: "Group", icon: <UsersThree size={14} weight="fill" /> },
                      ].map(mode => (
                        <button key={mode.key} onClick={() => { setSendMode(mode.key); setForm(prev => ({ ...prev, recipient_id: "", recipient_ids: [], recipient_group: "" })); }} data-testid={`send-mode-${mode.key}`} className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${sendMode === mode.key ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-200"}`}>
                          {mode.icon} {mode.label}
                        </button>
                      ))}
                    </div>
                    {sendMode === "individual" && (
                      <Select value={form.recipient_id} onValueChange={v => setForm({ ...form, recipient_id: v })}>
                        <SelectTrigger className="rounded-none" data-testid="msg-recipient-select"><SelectValue placeholder="Select recipient" /></SelectTrigger>
                        <SelectContent>{otherUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {sendMode === "multiple" && (
                      <div>
                        <Input placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="rounded-none mb-2" />
                        <div className="max-h-36 overflow-y-auto border border-slate-200 p-1 space-y-0.5">
                          {filteredUsers.map(u => (
                            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                              <input type="checkbox" checked={form.recipient_ids.includes(u.id)} onChange={() => toggleRecipient(u.id)} className="accent-blue-800" />
                              <span className="truncate">{u.name}</span>
                              <Badge variant="outline" className="ml-auto text-[10px] rounded-none">{u.role}</Badge>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {sendMode === "group" && (
                      <Select value={form.recipient_group} onValueChange={v => setForm({ ...form, recipient_group: v })}>
                        <SelectTrigger className="rounded-none" data-testid="msg-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                        <SelectContent>{groupOptions.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="rounded-none" placeholder="Subject" data-testid="msg-subject-input" />
                    <MentionTextarea
                      value={form.content}
                      onChange={e => setForm({ ...form, content: e.target.value })}
                      className="min-h-[100px]"
                      rows={5}
                      placeholder="Write your message... Use @name to tag someone."
                      data-testid="msg-content-input"
                    />
                    <div className="flex items-center justify-end -mt-1">
                      <EmojiButton onSelect={(emoji) => setForm(prev => ({ ...prev, content: (prev.content || "") + emoji }))} testId="compose-emoji-btn" />
                    </div>
                    {form.attachment_url ? (
                      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2">
                        <FileIcon size={14} className="text-blue-800" /><span className="text-xs truncate flex-1">{form.attachment_name}</span>
                        <button onClick={() => setForm({ ...form, attachment_url: "", attachment_name: "" })}><X size={14} /></button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 cursor-pointer hover:bg-slate-50">
                        <Paperclip size={14} className="text-slate-500" />
                        <span className="text-xs text-slate-500">{uploading ? "Uploading..." : "Attach a file"}</span>
                        <input type="file" className="hidden" disabled={uploading} ref={fileInputRef} onChange={e => handleUpload(e.target.files?.[0])} data-testid="msg-attachment-input" />
                      </label>
                    )}
                    <Button onClick={sendMessage} disabled={sending || uploading} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="send-message-btn">
                      <PaperPlaneRight size={16} /> {sending ? "Sending..." : "Send Message"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search conversations..." className="rounded-none pl-8 h-8 text-xs" data-testid="conversation-search" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              [1,2,3].map(i => <div key={i} className="h-16 bg-slate-50 animate-pulse m-1" />)
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8">
                <EnvelopeSimple size={32} className="text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500">No conversations yet</p>
              </div>
            ) : filteredConversations.map((conv, idx) => {
              const isUnread = conv.recipient_id === userId && !conv.read;
              const isSelected = selectedMsg?.other_id === conv.other_id;
              return (
                <div key={conv.id} onClick={() => openConversation(conv)} data-testid={`conversation-${idx}`} className={`px-3 py-3 cursor-pointer border-b border-slate-100 transition-all hover:bg-slate-50 ${isSelected ? "bg-blue-50 border-l-2 border-l-blue-800" : ""}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isUnread ? "bg-blue-800 text-white" : "bg-slate-200 text-slate-600"}`}>
                      {conv.other_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm truncate ${isUnread ? "font-semibold text-slate-900" : "text-slate-700"}`}>{conv.other_name}</p>
                        <span className="text-[10px] text-slate-400 flex-shrink-0">{new Date(conv.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{conv.content?.slice(0, 50)}</p>
                    </div>
                    {isUnread && <Circle size={8} weight="fill" className="text-blue-800 flex-shrink-0" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col min-h-0 bg-slate-50 ${selectedMsg ? "flex" : "hidden sm:flex"}`}>
          {selectedMsg ? (
            <>
              {/* Chat Header - sticky at top of chat area */}
              <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 sticky top-0 z-10">
                {/* Back button visible only on mobile */}
                <button
                  onClick={() => setSelectedMsg(null)}
                  className="sm:hidden p-1 -ml-1 text-slate-500 hover:text-slate-900"
                  data-testid="chat-back-btn"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="w-9 h-9 rounded-full bg-blue-800 text-white flex items-center justify-center text-sm font-bold">
                  {selectedMsg.other_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 truncate">{selectedMsg.other_name}</p>
                  <p className="text-xs text-slate-500 truncate">{selectedMsg.subject}</p>
                </div>
              </div>

              {/* Messages */}
              <div ref={chatEndRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ minHeight: 0 }}>
                {thread.map((msg, i) => {
                  const isMine = msg.sender_id === userId;
                  return (
                    <div key={msg.id || i} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] ${isMine ? "bg-blue-800 text-white rounded-tl-lg rounded-tr-lg rounded-bl-lg" : "bg-white border border-slate-200 text-slate-700 rounded-tl-lg rounded-tr-lg rounded-br-lg"} px-3 py-2 shadow-sm`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.attachment_url && (
                          <a href={msg.attachment_url} target="_blank" rel="noreferrer" className={`flex items-center gap-1.5 mt-2 text-xs ${isMine ? "text-blue-200 hover:text-white" : "text-blue-800 hover:underline"}`}>
                            <Paperclip size={12} /> {msg.attachment_name || "Attachment"}
                          </a>
                        )}
                        <p className={`text-[10px] mt-1 ${isMine ? "text-blue-300" : "text-slate-400"}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={scrollAnchorRef} />
              </div>

              {/* Reply Input */}
              <div className="bg-white border-t border-slate-200 px-3 py-2 flex-shrink-0 relative">
                {replyAttachment.url && (
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-1.5 mb-2 text-xs">
                    <FileIcon size={12} className="text-blue-800" /><span className="truncate flex-1">{replyAttachment.name}</span>
                    <button onClick={() => setReplyAttachment({ url: "", name: "" })}><X size={12} /></button>
                  </div>
                )}
                {showEmoji && (
                  <div className="absolute bottom-14 right-3 z-50">
                    <EmojiPicker onEmojiClick={(e) => { setReplyText(prev => prev + e.emoji); setShowEmoji(false); }} width={300} height={350} />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input type="file" className="hidden" ref={replyFileRef} onChange={e => handleUpload(e.target.files?.[0], true)} />
                  <button onClick={() => replyFileRef.current?.click()} className="text-slate-400 hover:text-slate-600 p-1.5" data-testid="reply-attach-btn" disabled={uploading}>
                    <Paperclip size={18} />
                  </button>
                  <button onClick={() => setShowEmoji(!showEmoji)} className="text-slate-400 hover:text-slate-600 p-1.5" data-testid="emoji-btn">
                    <Smiley size={18} />
                  </button>
                  <Input
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendReply()}
                    placeholder="Type a message..."
                    className="rounded-none flex-1 text-sm"
                    data-testid="reply-input"
                  />
                  <Button onClick={sendReply} disabled={sending || !replyText.trim()} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none px-3" data-testid="reply-send-btn">
                    <PaperPlaneRight size={16} weight="fill" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
              <EnvelopeOpen size={48} className="mb-3" />
              <p className="text-sm">Select a conversation or start a new one</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
